import data from "~/json/pro_card_labels.json";
import labelData from "~/json/pro_card_labels.json";
import proCardApplicationData from "~/json/pro_card_data.json";
import applicationData from "~/json/pro_card_application_data.json";
import {
  PROCARD_CONSTANTS,
  ProCardApplicationType,
  type ProCardApplicationStatus,
  type ProCardFormData,
  type ProCardFormStep,
} from "~/types/ecom/procard.type";
import type { Prettify } from "~/types/shared.type";

const defaultFormData: Prettify<ProCardFormData> = {
  verify_age: false,
  terms_accepted: false,
  company_id: "",
  commerce_number_search_item: "",
  company_name_search_item: "",
  company_name: "",
  company_duns: "",
  company_address_line1: "",
  company_address_city: "",
  company_incorporated_date: "",
  current_step: 0,
  position_within_business: "",
  company_vat: "",
  company_country: "",
  company_postcode: "",
  company_registration_number: "",
  account_holder_name: "",
  phone_number: "",
  bank_details: {
    account_holder_name: "",
    iban: "",
    type: "Company",
    bank: {
      Name: "",
      Bic: "",
    },
  },
};

export const useProCardStore = defineStore("pro-card", {
  state: () => ({
    pro_card_type: "",
    current_step: <ProCardFormStep | null>null,
    application_form: defaultFormData,
    application_status: "draft" as ProCardApplicationStatus,
    commerce_number: "",
    company_name: "",
    modal_visible: { terms: false },
    loading: {
      next_button: false,
      save_button: false,
      application_status: false,
      company_list: false,
      bank_list: false,
      draft: false,
    },
    errors: {
      company_details: "",
      account_holder_name: "",
      agreement: "",
      iban: "",
    },
    procard_consent_data: [],

    // old states
    application_labels_data: { ...labelData },
    pro_application_data: { ...applicationData },
  }),
  actions: {
    async getCompanyList(
      name: string,
      number: string,
      countryCode: string = PROCARD_CONSTANTS.countryISOAlpha2Code
    ) {
      const { fetchCompanyList } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );
      return await fetchCompanyList(name, number, countryCode);
    },

    async processCompanyDetails(company: Record<string, any>) {
      const { fetchCompanyDetails } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );
      return await fetchCompanyDetails(company);
    },

    async validateAccountHolderName(duns: string, name: string) {
      const { accountHolderVerify } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );
      return await accountHolderVerify(duns, name);
    },
    // --------------------------------------------------------------- Old store code - needs to be refactored
    async saveProCardData(
      application_type = ProCardApplicationType.WITH_CREDIT
    ) {
      const { saveData } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );
      return await saveData(application_type);
    },

    async getDraftData() {
      const { fetchDraftData } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );

      const response = await fetchDraftData();

      if (response && response.data && response.data.application_data) {
        let parsed_data = JSON.parse(response.data.application_data);

        if (parsed_data.current_step) {
          this.application_form = parsed_data;
        }
      }

    },

    async submitApplicationData() {
      const { handleSubmitApplication } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );
      return await handleSubmitApplication();
    },

    async getBankDetails() {
      const { fetchDetails } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );
      return await fetchDetails();
    },

    async redirectToThankyou(application_type: string) {
      const { navigateToThankyou } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );
      return await navigateToThankyou(application_type);
    },

    async handleRedirectToTweeky() {
      const { navigateToTweeky } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );
      return await navigateToTweeky();
    },

    async fetchApplicationStatus() {
      const { getApplicationStatus } = await import(
        "../stores-datalayer/pro-card.datalayer"
      );
      return await getApplicationStatus();
    },

    emptyForm() {
      this.application_form = {
        verify_age: false,
        terms_accepted: false,
        company_id: "",
        commerce_number_search_item: "",
        company_name_search_item: "",
        company_name: "",
        company_duns: "",
        company_address_line1: "",
        company_address_city: "",
        company_incorporated_date: "",
        current_step: 0,
        position_within_business: "",
        company_vat: "",
        company_country: "",
        company_postcode: "",
        company_registration_number: "",
        account_holder_name: "",
        phone_number: "",
        bank_details: {
          account_holder_name: "",
          iban: "",
          type: "Company",
          bank: {
            Name: "",
            Bic: "",
          },
        },
      };
    },

    resetPendingAndErrors() {
      type ErrorStateKey = keyof typeof this.errors;
      type LoadingStateKey = keyof typeof this.loading;
      for (const field in this.errors) {
        this.errors[field as ErrorStateKey] = "";
      }
      for (const state in this.loading) {
        this.loading[state as LoadingStateKey] = false;
      }
    },
    async getProCardTermsAndCondition(apiKey: any, opts: object) {
      const {
        getProCardPopUpConsentData
    } = await import("@/services-optimized/builderService")
      this.procard_consent_data = await getProCardPopUpConsentData(apiKey, opts);
    },
    emptyProCardStore() {
      this.loading.draft = false;
      this.application_labels_data = data;
      this.pro_application_data = proCardApplicationData;
      this.resetPendingAndErrors();
    },
  },
});


if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProCardStore, import.meta.hot));
}
